/**
 * Plan of Record — list view. Every PoR in the workspace, with a meaningful
 * empty state for the first-run case. Marks and assigns the workspace primary.
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Compass, ClipboardList, Star } from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import type { PorRecord } from '../../types/plan-of-record';
import { listPors, setPrimaryPor } from '../../lib/planOfRecordService';
import { invalidateActivePorsCache } from '../../hooks/useActivePors';
import {
  PorCard,
  PorEmptyState,
  PorStatusPill,
  PrimaryButton,
  formatDate,
  relativeDays,
} from '../../components/plan-of-record/porUi';
import { usePlanInstrumentNav } from '../../contexts/PlanInstrumentNavContext';

export default function PorList() {
  const navigate = useNavigate();
  const { instrumentNewHref, porHref } = usePlanInstrumentNav();
  const [records, setRecords] = useState<PorRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null);

  const reload = async () => {
    const { data, error: err } = await listPors();
    if (err) setError(err);
    setRecords(data);
    setLoading(false);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error: err } = await listPors();
      if (!alive) return;
      if (err) setError(err);
      setRecords(data);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handleSetPrimary = async (porId: string) => {
    setSettingPrimaryId(porId);
    setError(null);
    const { error: err } = await setPrimaryPor(porId);
    setSettingPrimaryId(null);
    if (err) {
      setError(err);
      return;
    }
    invalidateActivePorsCache();
    await reload();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ClipboardList className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50 dark:bg-[var(--surface)]"
        title="Plan of Record"
        subtitle="What was decided, why, and by whom — and the basis for judging whether a proposed change is legitimate (Condition A) or a deviation (Conditions B, C, D). One record is the workspace primary; entry points open that one by default."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/prioritization/settings/plan-of-record"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
            >
              Page settings
            </Link>
            <PrimaryButton onClick={() => navigate(instrumentNewHref)}>
              <span className="inline-flex items-center gap-1.5">
                <Plus className="h-4 w-4" /> New Plan of Record
              </span>
            </PrimaryButton>
          </div>
        }
      />

      {error && (
        <div className="mb-4 border border-luminous-coral-200 dark:border-luminous-coral-800 bg-luminous-coral-50 dark:bg-luminous-coral-900/40 px-4 py-2.5 text-sm text-luminous-coral-800 dark:text-luminous-coral-100">
          {error}
        </div>
      )}

      {loading ? (
        <PorCard>
          <p className="text-sm text-[var(--text-ter)]">Loading…</p>
        </PorCard>
      ) : records.length === 0 ? (
        <PorCard>
          <PorEmptyState
            icon={<Compass className="h-7 w-7" />}
            title="No Plan of Record yet"
            prompt="A Plan of Record holds a commitment and its rationale after the decision is made. Create one to give the organization a baseline — without it, every condition looks the same."
            action={
              <PrimaryButton onClick={() => navigate(instrumentNewHref)}>
                Create the first Plan of Record
              </PrimaryButton>
            }
          />
        </PorCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {records.map((r) => (
            <div
              key={r.id}
              className={`bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]  border p-5 ${
                r.is_primary
                  ? 'border-primary-300 dark:border-primary-700 ring-1 ring-primary-100 dark:ring-primary-900/40'
                  : 'border-[var(--rule)] dark:border-[var(--rule)]'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  onClick={() => navigate(porHref(r.id, 'dashboard'))}
                  className="min-w-0 text-left group"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate group-hover:text-primary-700 dark:group-hover:text-primary-300">
                      {r.title}
                    </h3>
                    {r.is_primary && (
                      <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-primary-50 dark:bg-primary-950/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
                        <Star className="h-3 w-3 fill-current" />
                        Primary
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] font-mono mt-0.5">
                    v{r.version} · phase {r.current_phase}/{r.total_phases}
                  </p>
                </button>
                <PorStatusPill status={r.status} />
              </div>

              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-4 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  <span>Updated {formatDate(r.last_updated)}</span>
                  {r.next_review && <span>Next review {relativeDays(r.next_review)}</span>}
                </div>
                {!r.is_primary && (
                  <button
                    type="button"
                    onClick={() => void handleSetPrimary(r.id)}
                    disabled={settingPrimaryId === r.id}
                    className="inline-flex items-center gap-1 shrink-0 text-xs font-medium text-primary-600 hover:text-primary-800 disabled:opacity-60 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    <Star className="h-3.5 w-3.5" />
                    {settingPrimaryId === r.id ? 'Setting…' : 'Set as primary'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
